import { Calendar } from 'primereact/calendar';
import { useCustodia } from './useCustodia';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { CustodiaFormulario } from './CustodiaFormulario';
import { CustodiaTrazabilidad } from './CustodiaTrazabilidad';
import { CustodiaEnvioConfig } from './CustodiaEnvioConfig';
import { ESTADO_CREADO_CODE, ESTADO_EN_INICIADO_CODE, ESTADO_FINALIZADO_CODE } from './data/custodiaDicctionary';
import { SubirFotoTrazabilidad } from './SubirFotoTrazabilidad';
import { getPantallaActiva } from '../../util/permisosPantalla';
import { reenviarReporteCustodia, descargarReporteCustodia } from '../../services/Custodia/CustodiaService';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';

const esAdmin = () => (localStorage.getItem('roles') || '').replaceAll('"', '').includes('ADMINISTRADOR');

// PERMISO_OPERATIVO gatea VER la columna "Estado envío" (el operador lo conserva,
// para enterarse de que una custodia quedó retenida y avisar al supervisor).
const tienePermisoOperativo = () => {
    if (esAdmin()) return true;
    const p = getPantallaActiva({ componente: 'Custodia' });
    return !!(p && Array.isArray(p.permisosOtorgados) && p.permisosOtorgados.includes('PERMISO_OPERATIVO'));
};

// PERMISO_ENVIO gatea REENVIAR / DESCARGAR el reporte: solo el supervisor de consola.
const tienePermisoEnvio = () => {
    if (esAdmin()) return true;
    const p = getPantallaActiva({ componente: 'Custodia' });
    return !!(p && Array.isArray(p.permisosOtorgados) && p.permisosOtorgados.includes('PERMISO_ENVIO'));
};

const Custodia = () => {
    const {
        subirFotoTrazabilidad,
        setSubirFotoTrazabilidad,
        isLoading,
        fecha,
        onChangeFecha,
        custodiaData,
        custodiaSelected,
        setCustodiaSelected,
        onStartCreateCustodia,
        loadTrazabilidadHistory,
        setLoadTrazabilidadHistory,
        convertirFechaAISO,
        onStartEditCustodia,
        onSuccessSave,
        estadoCustodia,
        recargarLista
    } = useCustodia();
    const toast = useRef(null);
    const dt = useRef(null);

    const [onlyRead, setOnlyRead] = useState(true);
    const [showConfig, setShowConfig] = useState(false);
    const [reenviandoId, setReenviandoId] = useState(null);
    // Diálogo de equipos: aparece cuando la custodia queda retenida. Permite desmarcar
    // señuelos y/o la patrulla (salen del reporte y no se validan) y reenviar o descargar.
    const [dlgEquipos, setDlgEquipos] = useState(null); // { rowData, equipos, motivo, coberturaMinima, coberturaForzarMin }
    const [excluidos, setExcluidos] = useState([]);     // dispositivoId desmarcados
    const [descargando, setDescargando] = useState(false);

    const puedeOperar = tienePermisoOperativo(); // ver columna "Estado envío"
    const puedeEnviar = tienePermisoEnvio();     // reenviar / descargar (supervisor de consola)

    useEffect(() => {
        const role = localStorage.getItem('roles');

        const rolesArray = role.replaceAll('"', '');

        if (rolesArray.includes('ADMINISTRADOR') || rolesArray.includes('CUSTODIO')) {
            setOnlyRead(false);
        }
    }, []);

    const SEVERIDAD_ENVIO = { ENVIADA: 'success', RETENIDA: 'warning', FALLO: 'danger', NO_ENVIADO: 'info' };

    const estadoEnvioBody = (rowData) => {
        if (!rowData.estadoEnvio) return <span className="text-color-secondary">—</span>;
        return (
            <span title={rowData.motivoEnvio || ''} style={{ cursor: rowData.motivoEnvio ? 'help' : 'default' }}>
                <Tag value={rowData.estadoEnvio} severity={SEVERIDAD_ENVIO[rowData.estadoEnvio] || 'info'} />
            </span>
        );
    };

    // desdeDialogo=false → clic ✈️ de la tabla: si queda retenida (forzable o no) SIEMPRE
    //   abre el diálogo de equipos para desmarcar; solo envía directo si la cobertura está OK.
    // desdeDialogo=true → botón "Reenviar" dentro del diálogo: ahí sí, si es forzable, pide
    //   la confirmación de "enviar de todas formas".
    const onReenviar = async (rowData, forzar = false, excluir = excluidos, desdeDialogo = false) => {
        try {
            setReenviandoId(rowData.id);
            const resp = await reenviarReporteCustodia(rowData.id, forzar, excluir);
            const r = resp.data || {};

            // Envío OK: se mandó al cliente.
            if (!r.retenido) {
                setDlgEquipos(null);
                setExcluidos([]);
                toast.current?.show({
                    severity: 'success',
                    summary: forzar ? 'Reporte enviado al cliente (envío forzado)' : 'Reporte reenviado al cliente',
                    detail: excluir?.length ? `Se excluyeron ${excluir.length} equipo(s) del reporte.` : undefined,
                    life: 4000
                });
                recargarLista();
                return;
            }

            // Sin ningún equipo marcado: no se envía un reporte sin GPS, solo se puede descargar.
            if (r.sinEquipos) {
                setDlgEquipos({ rowData, ...r });
                toast.current?.show({ severity: 'warn', summary: 'No se puede enviar', detail: r.motivo, life: 9000 });
                return;
            }

            // Retenida. Desde la tabla: abrir el diálogo de equipos (no forzar todavía).
            if (!desdeDialogo) {
                setDlgEquipos({ rowData, ...r });
                setExcluidos((r.equipos || []).filter((e) => e.excluido).map((e) => e.dispositivoId));
                return;
            }

            // Retenida pero forzable (todos los marcados ≥ piso): pedir confirmación para enviar igual.
            if (r.forzable && !forzar) {
                const min = r.coberturaMinima ?? 80;
                const piso = r.coberturaForzarMin ?? 50;
                confirmDialog({
                    header: 'Cobertura GPS incompleta',
                    message: `${r.motivo}\n\nLa cobertura está por debajo del mínimo (${min}%) pero por encima del piso permitido (${piso}%). ¿Deseás enviar el reporte al cliente de todas formas?`,
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Enviar de todas formas',
                    rejectLabel: 'Cancelar',
                    acceptClassName: 'p-button-warning',
                    accept: () => onReenviar(rowData, true, excluir, true)
                });
                return;
            }

            // Retenida y algún marcado bajo el piso: refrescar el diálogo con el aviso de cuál quitar.
            setDlgEquipos({ rowData, ...r });
            toast.current?.show({ severity: 'warn', summary: 'No se puede enviar', detail: r.motivo, life: 9000 });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error al reenviar', detail: 'Intente nuevamente.', life: 4000 });
        } finally {
            setReenviandoId(null);
        }
    };

    // Descarga el PDF con los equipos desmarcados fuera. Es la salida manual cuando no
    // se puede enviar al cliente (p. ej. ningún GPS con datos).
    const onDescargar = async (rowData, excluir = excluidos) => {
        try {
            setDescargando(true);
            const resp = await descargarReporteCustodia(rowData.id, excluir);
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `reporte-custodia-${rowData.id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.current?.show({ severity: 'success', summary: 'Reporte descargado', detail: 'Enviálo manualmente al cliente.', life: 4000 });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error al descargar', detail: 'Intente nuevamente.', life: 4000 });
        } finally {
            setDescargando(false);
        }
    };

    const toggleExcluido = (dispositivoId) => {
        setExcluidos((prev) => (prev.includes(dispositivoId) ? prev.filter((id) => id !== dispositivoId) : [...prev, dispositivoId]));
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <>
                <Button icon={`${onlyRead ? 'pi pi-eye' : 'pi pi-pencil'}`} className="p-button-rounded p-button-info mr-2" tooltip="Editar" tooltipOptions={{ position: 'left' }} onClick={() => onStartEditCustodia(rowData)} />
                {rowData.estado?.codigo === ESTADO_EN_INICIADO_CODE && (
                    <Button icon="pi pi-camera" className="p-button-rounded p-button-success mr-2" tooltip="Tomar Foto" tooltipOptions={{ position: 'left' }} onClick={() => setSubirFotoTrazabilidad(rowData)} disabled={onlyRead} />
                )}
                {rowData.estado?.codigo !== ESTADO_CREADO_CODE && (
                    <Button icon="pi pi-map" className="p-button-rounded p-button-warning" tooltip="Trazabilidad" tooltipOptions={{ position: 'left' }} onClick={() => setLoadTrazabilidadHistory(rowData)} />
                )}
                {puedeEnviar && rowData.estado?.codigo === ESTADO_FINALIZADO_CODE && (
                    <Button icon="pi pi-send" className="p-button-rounded p-button-help ml-2" tooltip="Validar y enviar reporte al cliente" tooltipOptions={{ position: 'left' }} loading={reenviandoId === rowData.id} onClick={() => { setExcluidos([]); onReenviar(rowData, false, []); }} />
                )}
            </>
        );
    };

    return (
        <div className="grid">
            <ConfirmDialog />
            <div className="col-12">
                <div className="card p-fluid col-12">
                    <h5>Custodia</h5>
                    <div className="col-12 flex align-items-end flex-column md:flex-row lg:flex-row sm:flex-row">
                        <div className="field col-12 md:col-6 lg:col-3 sm:col-6">
                            <label htmlFor="icon">Fecha</label>
                            <Calendar id="icon" value={fecha} /* disabled={habilitarCampo} */ onChange={onChangeFecha} showIcon dateFormat="dd/mm/yy" />
                        </div>
                        <div className="field col-12 md:col-6 lg:col-3 sm:col-6">
                            <Button label="Crear Custodia" icon="pi pi-plus" className="p-button-raised p-button-rounded" onClick={() => onStartCreateCustodia()} disabled={!fecha || onlyRead} />
                        </div>
                        {esAdmin() && (
                            <div className="field col-12 md:col-6 lg:col-3 sm:col-6">
                                <Button label="Configuración" icon="pi pi-cog" className="p-button-secondary p-button-outlined" onClick={() => setShowConfig(true)} />
                            </div>
                        )}
                    </div>
                </div>
                <div className="card p-fluid">
                    <h5>Lista de Custodias</h5>
                    <div className="col-12">
                        <Toast ref={toast} />
                        <DataTable
                            ref={dt}
                            paginator
                            rows={5}
                            rowsPerPageOptions={[5, 10]}
                            scrollable
                            scrollHeight="400px"
                            value={custodiaData}
                            // selection={contenedorSeleccionado}
                            // onSelectionChange={onChangeContenedorSeleccionado}
                            dataKey="bitacoraDetsId"
                            // filters={filters}
                            filterDisplay="row"
                            globalFilterFields={['booking']}
                            // header={header}
                            responsiveLayout="scroll"
                            sortField="fechaCreacion"
                            sortOrder={-1}
                            loading={isLoading}
                        >
                            <Column field="fechaCreacion" header="Fecha Ingreso" style={{ minWidth: '12rem' }} />
                            <Column field="nombreCliente" header="Cliente" style={{ minWidth: '12rem' }} />
                            <Column field="fechaInicio" header="Fecha Inicio" style={{ minWidth: '12rem' }} />
                            <Column field="origen" header="Origen" style={{ minWidth: '12rem' }} />
                            <Column field="fechaFin" header="Fecha Fin" style={{ minWidth: '12rem' }} />
                            <Column field="destino" header="Destino" style={{ minWidth: '12rem' }} />
                            <Column field="estado.nombre" header="Estado" style={{ minWidth: '12rem' }} />
                            {puedeOperar && <Column body={estadoEnvioBody} header="Estado envío" style={{ minWidth: '10rem' }} />}
                            <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '10rem' }} />
                        </DataTable>
                    </div>
                </div>
                <CustodiaFormulario
                    estadosList={estadoCustodia}
                    custodiaSeleccionada={custodiaSelected}
                    onClose={() => setCustodiaSelected(null)}
                    onSuccessSave={onSuccessSave}
                    toast={toast}
                    convertirFechaAISO={convertirFechaAISO}
                    onlyRead={onlyRead}
                />
                <CustodiaTrazabilidad custodiaSeleccionada={loadTrazabilidadHistory} onHide={() => setLoadTrazabilidadHistory(null)} />
                <SubirFotoTrazabilidad custodiaSeleccionada={subirFotoTrazabilidad} onHide={() => setSubirFotoTrazabilidad(null)} toast={toast} />
                <CustodiaEnvioConfig visible={showConfig} onHide={() => setShowConfig(false)} />

                {/* Equipos de la custodia retenida: desmarcar saca al equipo del reporte y de la
                    validación. Sin ninguno marcado solo se puede descargar el PDF. */}
                <Dialog
                    header="Equipos GPS de la custodia"
                    visible={!!dlgEquipos}
                    style={{ width: '46rem', maxWidth: '95vw' }}
                    modal
                    onHide={() => { setDlgEquipos(null); setExcluidos([]); }}
                >
                    {dlgEquipos && (
                        <>
                            <div className="p-3 mb-3 border-round" style={{ background: 'var(--surface-100)' }}>
                                <i className="pi pi-exclamation-triangle mr-2" style={{ color: 'var(--yellow-600)' }} />
                                {dlgEquipos.motivo}
                            </div>
                            <p className="text-sm text-color-secondary mt-0">
                                Desmarque los equipos que no funcionaron: salen del reporte (sin nombre, fotos ni
                                seguimiento) y no se validan. Mínimo <b>{dlgEquipos.coberturaMinima ?? 80}%</b>, piso
                                para enviar <b>{dlgEquipos.coberturaForzarMin ?? 50}%</b>.
                            </p>

                            {(dlgEquipos.equipos || []).map((eq) => {
                                const incluido = !excluidos.includes(eq.dispositivoId);
                                const bajoPiso = eq.coberturaPct < (dlgEquipos.coberturaForzarMin ?? 50);
                                return (
                                    <div key={eq.dispositivoId} className="flex align-items-center py-2 border-bottom-1 surface-border">
                                        <Checkbox inputId={`eq-${eq.dispositivoId}`} checked={incluido} onChange={() => toggleExcluido(eq.dispositivoId)} />
                                        <label htmlFor={`eq-${eq.dispositivoId}`} className="ml-2 flex-1 cursor-pointer">
                                            <span className="font-medium">{eq.nombre}</span>
                                            <Tag className="ml-2" severity={eq.tipo === 'PATRULLA' ? 'info' : 'secondary'} value={eq.tipo} />
                                        </label>
                                        <Tag
                                            severity={eq.ok ? 'success' : bajoPiso ? 'danger' : 'warning'}
                                            value={`${eq.coberturaPct}%`}
                                        />
                                        {incluido && bajoPiso && (
                                            <small className="ml-2 text-red-500">quitar para enviar</small>
                                        )}
                                    </div>
                                );
                            })}

                            <div className="flex justify-content-end gap-2 mt-4">
                                <Button label="Cancelar" className="p-button-text" onClick={() => { setDlgEquipos(null); setExcluidos([]); }} />
                                <Button
                                    label="Descargar PDF"
                                    icon="pi pi-download"
                                    className="p-button-secondary"
                                    loading={descargando}
                                    onClick={() => onDescargar(dlgEquipos.rowData)}
                                />
                                <Button
                                    label="Reenviar al cliente"
                                    icon="pi pi-send"
                                    loading={reenviandoId === dlgEquipos.rowData.id}
                                    disabled={(dlgEquipos.equipos || []).length === excluidos.length}
                                    onClick={() => onReenviar(dlgEquipos.rowData, false, excluidos, true)}
                                />
                            </div>
                        </>
                    )}
                </Dialog>
            </div>
        </div>
    );
};
export default Custodia;
